import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../services/api';
import '../assets/FinanceICAssignmentModal.css';
import '../assets/IcAssignmentModal.css';

/**
 * Admin: per-invoice Finance IC / Payment IC assignment for ONE month.
 *
 * Shows every FINALIZED-onwards invoice for the selected month, each with its
 * CURRENT Finance IC and Payment IC (which default to the tutee's defaults set
 * in Admin → Tutors). Changing a row's dropdown OVERRIDES that single invoice
 * for this month only — saved immediately. An "Overridden" tag shows when a
 * row differs from the tutee's default.
 *
 * Drafts are excluded: they inherit the tutee default and get their IC set at
 * finalization, so there's nothing to override yet.
 */
const InvoiceIcOverrideModal = ({ isOpen, onClose, month, year, invoices = [], onApplied }) => {
  const [financeICs, setFinanceICs] = useState([]);
  const [paymentICs, setPaymentICs] = useState([]);
  // Local working copy of each invoice's current FIC/PIC so the UI updates
  // instantly after a save without refetching the whole page.
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null); // `${invId}:fic` | `${invId}:pic`
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false); // did anything change (for onApplied refresh)

  const monthName = new Date(2000, (month || 1) - 1).toLocaleString('en-US', { month: 'long' });

  const idOf = (ref) => (ref && typeof ref === 'object' ? ref._id : ref) || '';

  const buildRows = useCallback((invs) => {
    return invs
      .filter(inv => inv.status !== 'draft' && inv.status !== 'revised')
      .map(inv => ({
        _id: inv._id,
        invoiceNumber: inv.invoiceNumber,
        tuteeName: inv.tutee?.name || 'Tutee',
        status: inv.status,
        financeICId: idOf(inv.financeIC),
        paymentICId: idOf(inv.paymentIC),
        defaultFinanceICId: idOf(inv.tutee?.defaultFinanceIC),
        defaultPaymentICId: idOf(inv.tutee?.defaultPaymentIC)
      }));
  }, []);

  const loadPickLists = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [ficRes, picRes] = await Promise.all([
        adminApi.getEligibleFinanceICs(),
        adminApi.getPaymentICs()
      ]);
      setFinanceICs(ficRes.data?.data || ficRes.data || []);
      setPaymentICs(picRes.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load Finance IC / Payment IC lists');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError('');
      setSuccess('');
      setDirty(false);
      setRows(buildRows(invoices));
      loadPickLists();
    }
  }, [isOpen, invoices, buildRows, loadPickLists]);

  if (!isOpen) return null;

  const draftCount = invoices.filter(inv => inv.status === 'draft').length;

  const flash = (setter, msg, ms = 3000) => { setter(msg); setTimeout(() => setter(''), ms); };

  const changeFinanceIC = async (row, newId) => {
    setSavingId(`${row._id}:fic`);
    setError('');
    try {
      await adminApi.assignFinanceICToInvoice(row._id, newId || null);
      setRows(prev => prev.map(r => r._id === row._id ? { ...r, financeICId: newId } : r));
      setDirty(true);
      flash(setSuccess, `Finance IC updated for ${row.tuteeName}.`);
    } catch (err) {
      flash(setError, err.response?.data?.message || 'Failed to update Finance IC.', 4000);
    } finally {
      setSavingId(null);
    }
  };

  const changePaymentIC = async (row, newId) => {
    setSavingId(`${row._id}:pic`);
    setError('');
    try {
      await adminApi.assignPaymentICToInvoice(row._id, newId || null);
      setRows(prev => prev.map(r => r._id === row._id ? { ...r, paymentICId: newId } : r));
      setDirty(true);
      flash(setSuccess, `Payment IC updated for ${row.tuteeName}.`);
    } catch (err) {
      flash(setError, err.response?.data?.message || 'Failed to update Payment IC.', 4000);
    } finally {
      setSavingId(null);
    }
  };

  const handleClose = () => {
    if (dirty) onApplied && onApplied();
    onClose();
  };

  return (
    <div className="finance-ic-modal-overlay" onClick={handleClose}>
      <div className="finance-ic-modal ic-override-modal" onClick={e => e.stopPropagation()}>

        <div className="finance-ic-modal-header">
          <div className="header-content">
            <h2>Assign IC — {monthName} {year}</h2>
            <p className="header-subtitle">
              Each invoice defaults to the Finance IC / Payment IC set in Admin → Tutors.
              Change a row to override it for this month only.
              {draftCount > 0 && ` ${draftCount} draft(s) hidden (they use the tutee default until finalized).`}
            </p>
          </div>
          <button className="ic-modal-close" onClick={handleClose} aria-label="Close modal" type="button">
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="finance-ic-modal-content">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {loading ? (
            <p className="no-assignments">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="no-assignments">No finalized invoices this month to assign.</p>
          ) : (
            <div className="ic-override-scroll">
              <table className="ic-override-table">
                <thead>
                  <tr>
                    <th className="ico-tutee-col">Invoice / Tutee</th>
                    <th>Finance IC</th>
                    <th>Payment IC</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => {
                    const ficOverridden = row.defaultFinanceICId && row.financeICId
                      && String(row.financeICId) !== String(row.defaultFinanceICId);
                    const picOverridden = row.defaultPaymentICId && row.paymentICId
                      && String(row.paymentICId) !== String(row.defaultPaymentICId);
                    return (
                      <tr key={row._id}>
                        <td className="ico-tutee-col">
                          <span className="ico-tutee-name">{row.tuteeName}</span>
                          <span className="ico-invoice-no">{row.invoiceNumber}</span>
                        </td>
                        <td>
                          <select
                            className="ico-select"
                            value={row.financeICId || ''}
                            disabled={savingId === `${row._id}:fic`}
                            onChange={(e) => changeFinanceIC(row, e.target.value)}
                          >
                            <option value="">— None —</option>
                            {financeICs.map(ic => (
                              <option key={ic._id} value={ic._id}>{ic.name}</option>
                            ))}
                          </select>
                          {ficOverridden && <span className="ico-override-badge">Overridden</span>}
                        </td>
                        <td>
                          <select
                            className="ico-select"
                            value={row.paymentICId || ''}
                            disabled={savingId === `${row._id}:pic`}
                            onChange={(e) => changePaymentIC(row, e.target.value)}
                          >
                            <option value="">— None —</option>
                            {paymentICs.map(ic => (
                              <option key={ic._id} value={ic._id}>
                                {ic.paynowName} ({ic.paynowNumber})
                              </option>
                            ))}
                          </select>
                          {picOverridden && <span className="ico-override-badge">Overridden</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="finance-ic-modal-footer">
          <button className="btn btn-secondary" onClick={handleClose}>Done</button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceIcOverrideModal;
